import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Container,
  CircularProgress,
  Button,
} from '@mui/material';
import { CheckCircle, Error, Lock } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

const Unlock = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [username, setUsername] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setStatus('error');
        setMessage('No unlock token provided.');
        return;
      }

      try {
        const response = await authAPI.verifyUnlock(token);
        
        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message || 'Account unlocked successfully!');
          setUsername(response.data.username || '');
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Failed to unlock account.');
        }
      } catch (error) {
        setStatus('error');
        const errorMessage = error.response?.data?.message || 'Invalid or expired unlock token.';
        setMessage(errorMessage);
      }
    };

    verifyToken();
  }, [token]);

  const handleGoToLogin = () => {
    navigate('/login?unlocked=true');
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 450 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography component="h1" variant="h4" gutterBottom>
                Novo Solution
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Account Unlock
              </Typography>
            </Box>

            {status === 'verifying' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Verifying unlock token...
                </Typography>
              </Box>
            )}

            {status === 'success' && (
              <Box sx={{ textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                <Alert severity="success" sx={{ mb: 3 }}>
                  {message}
                </Alert>
                {username && (
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Welcome back, <strong>{username}</strong>!
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Your account has been unlocked. You can now log in with your credentials.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Lock />}
                  onClick={handleGoToLogin}
                  fullWidth
                >
                  Go to Login
                </Button>
              </Box>
            )}

            {status === 'error' && (
              <Box sx={{ textAlign: 'center' }}>
                <Error sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                <Alert severity="error" sx={{ mb: 3 }}>
                  {message}
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  The unlock link may have expired or already been used.
                  Please request a new unlock email if needed.
                </Typography>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  fullWidth
                >
                  Back to Login
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Unlock;

